import { Box, Link, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { StoryPageLayout } from '../layouts';

const PAGE_URL = 'https://www.rsmc.ai/ebooks/finance-first-cloud-management';
const PAGE_IMAGE = 'https://www.rsmc.ai/images/finops-team-celebration.jpg';
const PAGE_DESCRIPTION =
  'As organizations adopt AWS for innovation or to modernize legacy systems, their cloud bills reflect this maturity. This adoption increases the financial variables that need to be accounted for while managing cloud spend — including SaaS subscriptions, multi-cloud, AI tokens, or Marketplace purchases.';

// Common paragraph styling
const paragraphSx = {
  fontSize: '1rem',
  lineHeight: 1.8,
  color: 'var(--color-text)',
  mb: 3,
};

// Section heading styling
const headingSx = {
  fontWeight: 700,
  mb: 3,
  mt: 6,
  color: 'var(--color-heading)',
  fontSize: { xs: '1.25rem', md: '1.5rem' },
};

// Sub-heading inside a section
const subHeadingSx = {
  fontWeight: 700,
  mb: 2.5,
  mt: 4,
  color: 'var(--color-heading)',
  fontSize: '1.125rem',
};

// Link styling
const linkSx = {
  color: '#5c6bc0',
  textDecoration: 'underline',
  '&:hover': {
    color: '#3949ab',
  },
};

// Body copy inside cards and panels
const cardBodySx = {
  fontSize: '0.9375rem',
  lineHeight: 1.7,
  color: 'var(--color-text-muted)',
};

/**
 * Italic standfirst paragraph used to open a section.
 *
 * @param {Object} props - Component props
 * @param {import('react').ReactNode} props.children - The standfirst copy
 * @returns {import('react').ReactElement} A standfirst paragraph
 */
function Standfirst({ children }) {
  return (
    <Typography
      sx={{
        ...paragraphSx,
        fontSize: '1.0625rem',
        fontStyle: 'italic',
        color: 'var(--color-text-muted)',
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Left-aligned boxed pull quote with a large decorative quote mark.
 *
 * @param {Object} props - Component props
 * @param {string} props.quote - The quoted text
 * @returns {import('react').ReactElement} A boxed pull quote
 */
function QuoteBox({ quote }) {
  return (
    <Box
      component="figure"
      sx={{
        my: 6,
        mx: 0,
        p: { xs: 3, md: 4 },
        backgroundColor: 'var(--color-background-light)',
        border: '2px solid var(--color-primary)',
        borderRadius: '12px',
        position: 'relative',
      }}
    >
      <Typography
        component="span"
        aria-hidden="true"
        sx={{
          display: 'block',
          fontFamily: '"Playfair Display", serif',
          fontSize: '3.5rem',
          lineHeight: 0.6,
          color: 'var(--color-primary)',
          mb: 1.5,
        }}
      >
        &ldquo;
      </Typography>
      <Typography
        component="blockquote"
        sx={{
          fontSize: { xs: '1.25rem', md: '1.5rem' },
          lineHeight: 1.45,
          fontWeight: 600,
          color: 'var(--color-heading)',
          fontStyle: 'italic',
          m: 0,
        }}
      >
        {quote}
      </Typography>
    </Box>
  );
}

/**
 * Bordered panel. The default look is the RSMC green left rule; `outlined`
 * swaps it for a full green border with rounded corners.
 *
 * @param {Object} props - Component props
 * @param {import('react').ReactNode} props.heading - Panel heading
 * @param {boolean} [props.outlined] - Full green border with rounded corners
 * @param {import('react').ReactNode} props.children - Panel body
 * @returns {import('react').ReactElement} A bordered panel
 */
function Panel({ heading, outlined = false, children }) {
  return (
    <Box
      sx={{
        ...(outlined
          ? {
              border: '2px solid var(--color-primary)',
              borderRadius: '12px',
            }
          : {
              borderLeft: '4px solid var(--color-primary)',
              borderRadius: '0 12px 12px 0',
            }),
        backgroundColor:
          'color-mix(in srgb, var(--color-primary) 6%, transparent)',
        p: { xs: 2.5, md: 3 },
        mb: 2.5,
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          fontSize: '1.0625rem',
          color: 'var(--color-heading)',
          mb: 1.5,
        }}
      >
        {heading}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Tick list used inside panels.
 *
 * @param {Object} props - Component props
 * @param {string[]} props.items - List items
 * @returns {import('react').ReactElement} A tick list
 */
function CheckList({ items }) {
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {items.map((item) => (
        <Box
          component="li"
          key={item}
          sx={{ display: 'flex', gap: 1.5, mb: 1.25 }}
        >
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              color: 'var(--color-primary)',
              fontWeight: 700,
              lineHeight: 1.7,
            }}
          >
            &#10003;
          </Box>
          <Typography sx={cardBodySx}>{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Row of equal-weight cards, each a heading plus body.
 *
 * @param {Object} props - Component props
 * @param {{heading: string, body: string}[]} props.cards - The cards to render
 * @param {number} [props.columns] - Column count at md and above
 * @returns {import('react').ReactElement} A card row
 */
function CardRow({ cards, columns = 3 }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: `repeat(${columns}, 1fr)`,
        },
        gap: 2,
        mb: 3,
      }}
    >
      {cards.map((card) => (
        <Box
          key={card.heading}
          sx={{
            border: '1px solid var(--color-border)',
            borderRadius: '12px',
            backgroundColor: 'var(--color-card)',
            p: 2.5,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              color: 'var(--color-heading)',
              mb: 1,
            }}
          >
            {card.heading}
          </Typography>
          <Typography sx={cardBodySx}>{card.body}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Horizontal survey-result bars.
 *
 * One measure ("% of organizations") across a handful of named items, so every
 * bar carries the same hue - length is the only encoding, and the hue is never
 * used to re-state the value. Each row's label and value are real text in
 * reading order, so the figure is legible without seeing the bars at all, which
 * is also why it needs no tooltip layer.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Figure title
 * @param {{label: string, value: number}[]} props.rows - Rows, value as a percentage
 * @param {string} props.source - Sample size and source line
 * @returns {import('react').ReactElement} A bar figure
 */
function BarFigure({ title, rows, source }) {
  return (
    <Box
      component="figure"
      sx={{
        m: 0,
        my: 5,
        p: { xs: 2.5, md: 3.5 },
        backgroundColor: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: '12px',
      }}
    >
      <Typography
        component="figcaption"
        sx={{
          fontWeight: 700,
          fontSize: '1.0625rem',
          color: 'var(--color-heading)',
          textAlign: 'center',
          mb: 3,
        }}
      >
        {title}
      </Typography>

      {rows.map((row) => (
        <Box key={row.label} sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 700,
              color: 'var(--color-text)',
              mb: 0.75,
            }}
          >
            {row.label}
          </Typography>
          {/* Recessive rail; the fill is square at the baseline and rounded at
              the data end so the growing edge reads as the value. */}
          <Box
            sx={{
              height: '28px',
              borderRadius: '0 4px 4px 0',
              backgroundColor:
                'color-mix(in srgb, var(--color-text) 8%, transparent)',
            }}
          >
            <Box
              sx={{
                width: `${row.value}%`,
                height: '100%',
                borderRadius: '0 4px 4px 0',
                backgroundColor: 'var(--color-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                pr: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  color: 'var(--color-primary-text)',
                }}
              >
                {row.value}%
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}

      <Typography
        sx={{
          fontSize: '0.75rem',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
          mt: 2,
        }}
      >
        {source}
      </Typography>
    </Box>
  );
}

/**
 * The FinOps partnership triangle: technology and finance work together inside
 * the triangle, and the three FinOps phases sit at its corners. Based on the
 * Cloud Financial Management partnership in the AWS Well-Architected Framework
 * cost optimization pillar (source 4).
 *
 * @returns {import('react').ReactElement} The partnership figure (source: docs/design/finops-partnership-triangle.ai)
 */
function FinOpsTriangleDiagram() {
  return (
    <Box component="figure" sx={{ m: 0, my: 5 }}>
      <Box
        component="img"
        src="/images/finops-partnership-triangle.png"
        alt="The FinOps partnership triangle: overlapping technology-and-product and finance-and-business team circles sit inside a triangle whose edges connect the three FinOps phases — deploy, operate, and optimize."
        sx={{
          width: '100%',
          height: 'auto',
          maxWidth: '480px',
          mx: 'auto',
          display: 'block',
        }}
      />
      <Typography
        component="figcaption"
        sx={{
          fontSize: '0.8125rem',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
          mt: 1.5,
        }}
      >
        AWS Well-Architected Framework: Cost Optimization Pillar<sup>4</sup>
      </Typography>
    </Box>
  );
}

const reportingRequirements = [
  {
    heading: 'Finance professionals',
    body: 'Business-aligned metrics and clear reconciliation of cloud spend — without needing an AWS professional certification.',
  },
  {
    heading: 'Engineering teams',
    body: 'Architecture decisions made before they build, linking every technical choice to business value.',
  },
  {
    heading: 'Both groups',
    body: 'Shared visibility and role-based insights that create trusted recommendations that can be acted upon and governed.',
  },
];

const requestedFeatures = [
  'Granular monitoring of AI spend — tokens, LLM requests, and GPU utilization',
  'Shift-left: pre-deployment architecture costing',
  'A "single pane of glass" for different technology spend',
];

const financialExperiences = [
  {
    heading: 'Executive Financial Reporting',
    body: 'Get visibility into cloud spend in financial terms you understand. Make informed decisions about cloud investments with data aligned to your P&L and balance sheet.',
  },
  {
    heading: 'AI-Driven Business Intelligence',
    body: 'With our AI financial agents, easily generate "what-if" scenarios and real-time variance analysis — so finance teams focus on actionable insights rather than managing spreadsheets.',
  },
  {
    heading: 'Business Financials',
    body: 'Import current financial data for visualization and enrichment with your AWS spend data. Visualize your P&L data along with your AWS spend trends in the same business intelligence platform. We transform your spreadsheets into executive financial dashboards.',
  },
];

const discoveryOutputs = [
  'The dashboards and reports built for your financial operations',
  'The AI agents utilizing the knowledge base defined by you',
  'The automated workflows that connect cloud cost data to your existing financial systems',
  'The single business intelligence layer built for a modern FinOps framework',
];

const cloudServices = [
  { label: 'Data Warehouse', value: 71 },
  { label: 'DBaaS (Relational)', value: 64 },
  { label: 'Generative AI (GenAI)', value: 58 },
];

const cloudInitiatives = [
  { label: 'Optimizing cloud costs', value: 68 },
  { label: 'Migrating more workloads to the cloud', value: 49 },
  { label: 'Better financial reporting', value: 42 },
];

const sources = [
  {
    publisher: 'Flexera.',
    title: '2026 State of the Cloud Report',
    href: 'https://info.flexera.com/CM-REPORT-State-of-the-Cloud',
  },
  {
    publisher: 'FinOps Foundation.',
    title: 'State of FinOps 2026 Report',
    href: 'https://data.finops.org/',
  },
  {
    publisher: 'nOps.',
    title: 'AWS Reserved Instance and Savings Plan Changes',
    href: 'https://www.nops.io/blog/aws-reserved-instance-and-savings-plan-changes-for-2025/',
  },
  {
    publisher: 'Amazon Web Services.',
    title:
      'AWS Well-Architected Framework, Cost Optimization Pillar: Establish a Partnership Between Finance and Technology',
    href: 'https://docs.aws.amazon.com/wellarchitected/latest/cost-optimization-pillar/cost_cloud_financial_management_partnership.html',
  },
];

/**
 * Finance First Cloud Management eBook
 *
 * Long-form EllaFin eBook on why FinOps needs a single business intelligence
 * layer. Uses StoryPageLayout so the Early Access Program form sits in a sticky
 * sidebar beside the content.
 *
 * @returns {import('react').ReactElement} Finance First Cloud Management eBook page
 */
function FinanceFirstCloudManagement() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Transform Your Cloud Financial Management',
    description: PAGE_DESCRIPTION,
    author: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.rsmc.ai/images/r-logo.png',
      },
    },
    image: PAGE_IMAGE,
    datePublished: '2026-08-18',
    dateModified: '2026-08-18',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': PAGE_URL,
    },
  };

  return (
    <>
      <Helmet>
        <title>
          Finance First FinOps: Transform Your Cloud Financial Management | RSMC
        </title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          property="og:title"
          content="Transform Your Cloud Financial Management"
        />
        <meta
          property="og:description"
          content="Why FinOps needs one business intelligence layer — the EllaFin eBook on finance-first cloud management."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={PAGE_IMAGE} />
        <meta
          name="twitter:title"
          content="Finance First FinOps — an EllaFin eBook"
        />
        <meta
          name="twitter:description"
          content="Why FinOps needs one business intelligence layer for AWS spend and financial reporting."
        />
        <meta
          name="keywords"
          content="FinOps, EllaFin, cloud economics, AWS cost management, financial reporting, unit economics, cloud financial management, FP&A"
        />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <StoryPageLayout
        title="Finance First FinOps"
        subtitle="An EllaFin™ eBook"
        formHeading="Join the EllaFin™ Early Access Program"
        formFilePath="/forms/ellafin-early-access-form.txt"
        heroImage="/images/finops-team-celebration.jpg"
        heroImageAlt="Finance team applauding in an office meeting, with data analytics charts on the screens behind them"
        errorMessage="Unable to load the early access form"
        loadingMessage="Loading early access form..."
      >
        <Box component="article">
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 700,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              color: 'var(--color-primary)',
              mb: 2,
            }}
          >
            Finance-First FinOps · Cloud Economics
          </Typography>

          {/* Article Title */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              color: 'var(--color-heading)',
              mb: 3,
            }}
          >
            Transform Your Cloud Financial Management
          </Typography>

          <Standfirst>
            What if a budget-versus-cost report also included revenue? If cloud
            costs exceeded budget for a newly launched customer experience but
            revenue increased too, the conversation shifts from budget overage
            to business impact.
          </Standfirst>

          <Typography sx={paragraphSx}>
            As organizations adopt AWS for innovation or to modernize legacy
            systems, their cloud bills reflect this maturity. This adoption
            increases the financial variables that need to be accounted for
            while managing cloud spend — including SaaS subscriptions,
            multi-cloud, AI tokens, or Marketplace purchases.
          </Typography>

          <Typography sx={paragraphSx}>
            For finance teams, especially at small and medium-sized businesses,
            start-ups, or nonprofit organizations, the technical structure and
            variable consumption model of AWS adds complexity to their current
            financial operating model. AWS spend reconciliation that is trusted,
            comprehensive, and aligned to business metrics is now an
            organizational priority.
          </Typography>

          <QuoteBox quote="Cloud spend is not just an IT expense. With the right financial management in place, it becomes a strategic lever for growth and innovation." />

          <Typography sx={paragraphSx}>
            As companies continue to adopt cloud and AI as part of their
            innovation strategy, the cloud cost conversation shifts to the{' '}
            <strong>economics of cloud</strong>. Organizations want to report,
            measure, track, and align every variable that impacts profitability
            and company growth.
          </Typography>

          <BarFigure
            title="Public cloud services used by all organizations"
            rows={cloudServices}
            source="Currently in use · N=753 · Source: Flexera 2026 State of the Cloud Report (Figure 41)"
          />

          {/* Different systems, one budget */}
          <Typography variant="h2" sx={headingSx}>
            Different systems, one budget
          </Typography>

          <Typography sx={paragraphSx}>
            Organizations are investing more in cloud governance as the
            complexity of use increases. Recent surveys show 71% of
            organizations now have a Cloud Center of Excellence (CCOE)
            <sup>1</sup>, and 63% utilize a FinOps team. This shift shows a
            commitment to better collaboration and accountability — however,
            delivering financial insights remains challenging.
          </Typography>

          <Typography sx={paragraphSx}>
            Most organizations manage cloud spend and financial planning in
            separate systems. Finance teams use QuickBooks, Excel, Power BI,
            FP&amp;A, or ERP systems, while cloud spend is tracked in AWS Cost
            Explorer, CUR exports, Cloud Management Platforms, or custom-built
            tools.
          </Typography>

          <Typography variant="h3" sx={subHeadingSx}>
            What collaborative financial reporting requires
          </Typography>

          <CardRow cards={reportingRequirements} />

          <Panel
            outlined
            heading={
              <>
                Most requested FinOps tool features<sup>2</sup>
              </>
            }
          >
            <CheckList items={requestedFeatures} />
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: 'var(--color-text-muted)',
                mt: 1.5,
              }}
            >
              FinOps Foundation, State of FinOps 2026 Report<sup>2</sup>
            </Typography>
          </Panel>

          {/* Your FinOps practice framework */}
          <Typography variant="h2" sx={headingSx}>
            Your FinOps practice framework
          </Typography>

          <Typography sx={paragraphSx}>
            Cost optimization remains the top cloud priority for most
            organizations, but how success is measured is evolving. According to
            the Flexera 2026 State of the Cloud report, better financial
            reporting now ranks among the top three cloud initiatives across
            organizations — alongside cost optimization and continued cloud
            migration.
          </Typography>

          <Typography sx={paragraphSx}>
            This reflects a more mature FinOps approach, where success is no
            longer measured solely by cost efficiency, but also by business
            value delivered, competitive advantage gained, or speed of
            innovation. This evolution highlights the need to balance cost
            control with value delivery, ensuring FinOps governance aligns to
            business outcomes rather than just reducing spend.
          </Typography>

          <BarFigure
            title="Top cloud initiatives for all organizations"
            rows={cloudInitiatives}
            source="N=753 · Source: Flexera 2026 State of the Cloud Report (Figure 15)"
          />

          {/* Small teams, big responsibilities */}
          <Typography variant="h2" sx={headingSx}>
            Small teams, big responsibilities
          </Typography>

          <Typography sx={paragraphSx}>
            The path to unit economics begins with a clear understanding of your
            cloud cost, services, and savings opportunities. As programs like
            shared commitment discounts from AWS Resellers and MSPs are phased
            out, it's essential to manage your own commitments — establishing
            the visibility and automation those programs previously provided.
          </Typography>

          <Typography sx={paragraphSx}>
            The shift to managing your own commitments and AWS spend requires
            people, process, and tooling; however, FinOps teams tend to be
            small. Organizations with cloud spending under $15 million a year
            typically have an average of two to three practitioners — and their
            responsibilities are broadening to cover not just cloud costs, but
            also expenses related to AI, SaaS, licensing, and data center
            operations.
          </Typography>

          <FinOpsTriangleDiagram />

          {/* One business intelligence layer */}
          <Box sx={{ textAlign: 'center', mt: 6, mb: 1 }}>
            {/* White plate behind the lockup: the logo's navy wordmark is
                unreadable directly on the dark page (client-supplied layout) */}
            <Box
              sx={{
                display: 'inline-block',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                px: 3,
                py: 2,
              }}
            >
              <Box
                component="img"
                src="/logos/ellafin-logo.png"
                alt="EllaFin"
                sx={{ height: '44px', width: 'auto', display: 'block' }}
              />
            </Box>
          </Box>
          <Typography variant="h2" sx={headingSx}>
            One business intelligence layer. Three financial experiences.
          </Typography>

          <Typography sx={paragraphSx}>
            EllaFin&trade; is a business intelligence platform purpose-built to
            enable FinOps as a service for organizations utilizing AWS as their
            primary cloud provider. It checks every box needed to enable a
            sustainable FinOps framework.
          </Typography>

          {financialExperiences.map((item) => (
            <Panel key={item.heading} heading={item.heading}>
              <Typography sx={cardBodySx}>{item.body}</Typography>
            </Panel>
          ))}

          <Box component="figure" sx={{ m: 0, my: 5 }}>
            <Box
              component="img"
              src="/images/ellafin-nonprofit-edition-dashboards.jpg"
              alt="Three EllaFin dashboards from the Nonprofit Edition: donor engagement and donations, Amazon Connect ROI against donor impact, and an Amazon Connect cost tracker."
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: '12px',
                border: '1px solid var(--color-border)',
                display: 'block',
              }}
            />
          </Box>

          {/* Uniting AWS and FP&A data */}
          <Typography variant="h2" sx={headingSx}>
            Uniting AWS and FP&amp;A data
          </Typography>

          <Typography sx={paragraphSx}>
            Data needs context to provide insight or accelerate decision-making.
            We learn how your organization manages cloud operations and reports
            financial performance, then build an intelligence layer that
            reflects your reality. That discovery shapes everything we build:
          </Typography>

          <Panel heading="What discovery shapes">
            <CheckList items={discoveryOutputs} />
          </Panel>

          <Typography sx={{ ...paragraphSx, mt: 3 }}>
            EllaFin&trade; provides all the AI-powered features needed for
            modern financial reporting — but our as-a-service model recognizes
            that the power of the platform comes from the financial knowledge of
            its users. Operations change, business objectives adjust, and new
            financial data needs to be injected. EllaFin&trade; enables the
            continuous collaboration and financial insight organizations need
            for critical business decision making.
          </Typography>

          {/* Closing letter */}
          <Typography variant="h2" sx={headingSx}>
            RSMC is your trusted FinOps partner
          </Typography>

          <Typography sx={paragraphSx}>
            RSMC understands that for most organizations, adopting a cloud
            financial model is not a snap-of-the-fingers switch from CapEx to
            OpEx. It requires thoughtful coordination across teams, adaptation
            of existing financial practices, and a reporting approach that
            supports both operational agility and financial accountability.
          </Typography>

          <Typography sx={paragraphSx}>
            The alignment of cloud spend with existing financial operations can
            be a reality. Cloud spend is not just an IT expense. With the right
            financial management in place, it becomes a strategic lever for
            growth and innovation.
          </Typography>

          {/* FinOps listening session */}
          <Box sx={{ mt: 6 }}>
            <Panel heading="Participate in a FinOps Listening Session">
              <Typography sx={cardBodySx}>
                Receive a $500 voucher (while supplies last) for FinOps
                Foundation Certified AI Training.
              </Typography>
              <Link
                href="https://learn.finops.org/path/certified-finops-for-ai"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ ...linkSx, display: 'inline-block', mt: 1 }}
              >
                FinOps Certified for AI
              </Link>
            </Panel>
          </Box>

          {/* Sources */}
          <Box
            sx={{ mt: 6, pt: 3, borderTop: '1px solid var(--color-border)' }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--color-heading)',
                mb: 1.5,
              }}
            >
              Sources
            </Typography>
            <Box
              component="ol"
              sx={{
                m: 0,
                pl: 2.5,
                fontSize: '0.8125rem',
                color: 'var(--color-text-muted)',
                lineHeight: 1.9,
              }}
            >
              {sources.map((source) => (
                <li key={source.href}>
                  {source.publisher}{' '}
                  <Link
                    href={source.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={linkSx}
                  >
                    {source.title}
                  </Link>
                </li>
              ))}
            </Box>
          </Box>

          {/* Visual separator - R logo */}
          <Box sx={{ textAlign: 'center', mt: 6, mb: 2 }}>
            <Box
              component="img"
              src="/images/r-logo.png"
              alt="Ready Secure Managed Cloud"
              sx={{ width: '50px', height: 'auto', opacity: 0.7 }}
            />
          </Box>
        </Box>
      </StoryPageLayout>
    </>
  );
}

export default FinanceFirstCloudManagement;
